/** Notification localStorage persistence */

import {logger} from '@/lib/logger';
import type {Notification} from '@/store/notifications';

const log = logger.tag('NotificationStorage');

const STORAGE_KEY = 'modelbank_notifications';
const STORAGE_VERSION = '1.0';

interface StoredData {
    version: string;
    notifications: Notification[];
    lastUpdated: string;
}

/** Load notifications from localStorage */
export function loadNotifications(): Notification[] {
    if (typeof window === 'undefined') return [];

    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return [];

        const data: StoredData = JSON.parse(stored);

        // Clear old data if version mismatch
        if (data.version !== STORAGE_VERSION) {
            localStorage.removeItem(STORAGE_KEY);

            return [];
        }

        return data.notifications ?? [];
    } catch (error) {
        log.error('Failed to load from localStorage', error);

        return [];
    }
}

/** Save notifications to localStorage */
export function saveNotifications(notifications: Notification[]): void {
    if (typeof window === 'undefined') return;

    try {
        const data: StoredData = {
            version: STORAGE_VERSION,
            notifications,
            lastUpdated: new Date().toISOString(),
        };

        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
        log.error('Failed to save to localStorage', error);
    }
}

/** Clear all notifications from localStorage */
export function clearNotifications(): void {
    if (typeof window === 'undefined') return;

    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
        log.error('Failed to clear localStorage', error);
    }
}

/** Subscribe to cross-tab localStorage changes */
export function subscribeToStorageChanges(
    callback: (notifications: Notification[]) => void
): () => void {
    if (typeof window === 'undefined') return () => {};

    const handleStorageChange = (event: StorageEvent) => {
        if (event.key === STORAGE_KEY && event.newValue) {
            try {
                const data: StoredData = JSON.parse(event.newValue);
                callback(data.notifications ?? []);
            } catch (error) {
                log.error('Failed to parse storage change', error);
            }
        }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
        window.removeEventListener('storage', handleStorageChange);
    };
}
